import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Download, MapPin, X } from 'lucide-react';
import { resolveImgUrl } from '@/utils/imgUrl';
import { usePWAInstall } from '@/contexts/PWAInstallContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULTS = {
  logo_pesantren: '',
  nama_baris1: 'Pondok Pesantren',
  nama_baris2: "Baitul Qur'an",
  nama_baris3: 'Dabo Singkep',
  lokasi_pesantren: 'Kabupaten Lingga, Provinsi Kepulauan Riau.',
};

/** Deteksi iOS (iPhone/iPad/iPod) */
function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/** Deteksi Safari di iOS (bukan Chrome/Firefox wrapper) */
function detectIOSSafari() {
  if (!detectIOS()) return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua) && !/EdgiOS/.test(ua) && !/OPiOS/.test(ua);
}

/** App sudah berjalan sebagai PWA standalone */
function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

/** Ikon Share iOS — kotak dengan panah ke atas (mirip ikon asli Safari) */
function IOSShareIcon({ size = 18, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Kotak terbuka bawah */}
      <path d="M8 6H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3" />
      {/* Panah ke atas */}
      <polyline points="12 2 12 14" />
      <polyline points="8 6 12 2 16 6" />
    </svg>
  );
}

export default function WelcomePage() {
  const navigate = useNavigate();
  const { showInstall, handleInstall } = usePWAInstall();
  const [settings, setSettings] = useState(DEFAULTS);

  // Tampilkan banner iOS Safari jika: iOS + Safari + belum standalone + belum ditutup user
  const [showIOSBanner, setShowIOSBanner] = useState(() => {
    if (isStandalone()) return false;
    if (!detectIOSSafari()) return false;
    return sessionStorage.getItem('ios_banner_dismissed') !== '1';
  });

  const dismissIOSBanner = () => {
    sessionStorage.setItem('ios_banner_dismissed', '1');
    setShowIOSBanner(false);
  };

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(({ data }) => setSettings(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const logoSrc = settings.logo_pesantren
    ? resolveImgUrl(settings.logo_pesantren)
    : '/logo-zahbaq.png';

  const baris1 = settings.nama_baris1 || DEFAULTS.nama_baris1;
  const baris2 = settings.nama_baris2 || DEFAULTS.nama_baris2;
  const baris3 = settings.nama_baris3 || DEFAULTS.nama_baris3;
  const lokasi = settings.lokasi_pesantren || DEFAULTS.lokasi_pesantren;

  return (
    <div
      data-testid="welcome-page"
      className="relative min-h-screen bg-white flex flex-col overflow-hidden"
    >
      {/* ── Top-left organic decoration ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: -60,
          left: -70,
          width: 260,
          height: 280,
          background: 'linear-gradient(135deg, #a7f3d0 0%, #d1fae5 60%, #ecfdf5 100%)',
          borderRadius: '38% 62% 60% 40% / 35% 30% 70% 65%',
          opacity: 0.55,
        }}
      />
      {/* ── Top-right organic decoration ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: -45,
          right: -55,
          width: 190,
          height: 210,
          background: 'linear-gradient(225deg, #6ee7b7 0%, #a7f3d0 55%, #d1fae5 100%)',
          borderRadius: '60% 40% 35% 65% / 50% 60% 40% 50%',
          opacity: 0.35,
        }}
      />
      {/* ── Bottom-right mini accent ── */}
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: 110,
          right: -40,
          width: 120,
          height: 130,
          background: '#d1fae5',
          borderRadius: '50%',
          opacity: 0.3,
        }}
      />

      {/* ── Main content ── */}
      <div className="relative flex-1 flex flex-col px-8 pt-16 pb-36">
        {/* Logo + App name */}
        <div className="flex flex-col items-center mt-2 mb-10">
          <img
            src={logoSrc}
            alt="ZahBaq"
            className="w-24 h-24 object-contain mb-3"
            onError={e => { e.currentTarget.src = '/logo-zahbaq.png'; }}
          />
          <h1 className="font-playfair font-bold text-[28px] text-emerald-950 tracking-tight">
            ZahBaq
          </h1>
          <div className="flex items-center gap-3 mt-1.5">
            <div className="h-px w-8 bg-emerald-500 rounded-full" />
            <span className="text-[11px] text-emerald-600 font-medium tracking-wider lowercase">
              sistem manajemen tahfizh
            </span>
            <div className="h-px w-8 bg-emerald-500 rounded-full" />
          </div>
        </div>

        {/* Welcome label + Pesantren name */}
        <div className="flex-1 flex flex-col justify-center">
          <p
            data-testid="welcome-label"
            className="text-[10px] font-bold tracking-[0.28em] text-gray-400 uppercase mb-5"
          >
            Selamat Datang Di
          </p>

          <div className="mb-7" data-testid="welcome-title">
            <p className="font-playfair font-bold leading-[1.1] text-gray-900"
               style={{ fontSize: 'clamp(36px, 11vw, 50px)' }}>
              {baris1}
            </p>
            <p className="font-playfair font-bold leading-[1.1] text-emerald-600"
               style={{ fontSize: 'clamp(36px, 11vw, 50px)' }}>
              {baris2}
            </p>
            <p className="font-playfair font-bold leading-[1.1] text-gray-900"
               style={{ fontSize: 'clamp(36px, 11vw, 50px)' }}>
              {baris3}
            </p>
          </div>

          {/* Location */}
          <div className="flex items-start gap-3" data-testid="welcome-location">
            <MapPin size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-gray-500 text-sm leading-relaxed">{lokasi}</p>
          </div>
        </div>
      </div>

      {/* ── Fixed bottom buttons ── */}
      <div
        className="fixed bottom-0 left-0 right-0 px-6 bg-white"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 18px)', paddingTop: 14 }}
      >
        <div className="flex flex-col gap-2 max-w-sm mx-auto">
          <button
            data-testid="welcome-mulai-btn"
            onClick={() => navigate('/login')}
            className="w-full flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold py-3 rounded-xl transition-all active:scale-95 text-sm shadow-sm"
          >
            Mulai Sekarang
            <ArrowRight size={16} />
          </button>
          {showInstall && (
            <button
              data-testid="pwa-install-btn"
              onClick={handleInstall}
              className="w-full flex items-center justify-center gap-1.5 border-2 border-emerald-600 text-emerald-700 font-semibold py-3 rounded-xl transition-all hover:bg-emerald-50 active:scale-95 text-sm"
            >
              <Download size={16} />
              Pasang Aplikasi
            </button>
          )}
          {/* ── Banner petunjuk instalasi iOS Safari ── */}
          {showIOSBanner && (
            <div
              data-testid="ios-install-banner"
              className="relative bg-sky-50 border border-sky-200 rounded-2xl px-4 py-3 mt-1"
            >
              {/* Tombol tutup */}
              <button
                data-testid="ios-banner-dismiss"
                onClick={dismissIOSBanner}
                aria-label="Tutup banner"
                className="absolute top-2.5 right-2.5 text-sky-400 hover:text-sky-600 p-1 rounded-lg transition-colors"
              >
                <X size={14} />
              </button>
              <div className="flex items-start gap-3 pr-5">
                {/* Ikon Share iOS */}
                <span className="mt-0.5 text-sky-500 flex-shrink-0">
                  <IOSShareIcon size={22} />
                </span>
                <p className="text-xs text-sky-700 leading-relaxed">
                  Untuk pasang aplikasi di iPhone: tap tombol{' '}
                  <strong className="font-semibold">Share</strong>{' '}
                  <IOSShareIcon size={12} className="inline -mt-0.5" />{' '}
                  (kotak dengan panah ke atas) di bawah layar Safari, lalu pilih{' '}
                  <strong className="font-semibold">"Tambahkan ke Layar Utama"</strong>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
